import React, { useRef, useState } from 'react';
import Wrapper from '../assets/wrappers/LoginForm';
import Captcha, { CaptchaRef } from './Captcha'; import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';
import { login } from "../features/authSlice";
import { useDispatch } from 'react-redux';
import publicApi from '../services/publicApi';

const FaUser = require('react-icons/fa').FaUser;
const FaLock = require('react-icons/fa').FaLock;
const FaEye = require('react-icons/fa').FaEye;
const FaUndo = require('react-icons/fa').FaUndo;
const FaSignOutAlt = require('react-icons/fa').FaSignOutAlt;
const FaEyeSlash = require('react-icons/fa').FaEyeSlash;

const LoginForm: React.FC = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const dispatch = useDispatch();

    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [captchaId, setCaptchaId] = useState("");
    const passwordInputRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);
    const captchaRef = useRef<CaptchaRef>(null);
    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };
    type FormValues = {
        username: string;
        password: string;
        captchaInput: string;
    };
    const resetCaptcha = () => {
        formik.setFieldValue("captchaInput", "");
        captchaRef.current?.regenerate();
    };
    const errorMessages: Record<number, string> = {
        201: "Incorrect Username or Password",
        409: "Session expired. Please try again.",
        422: "Invalid input. Please check details.",
        429: "Too many attempts. Try again later.",
        500: "Internal Server Error",
    };

    const getPreloginToken = async () => {
        const response = await publicApi.post(
            `${baseUrl}api/prelogin-token`,
            {},
            {
                headers: {
                    "Content-Type": "application/json",
                },
                withCredentials: true,
            }
        );
        if (response?.data?.msg_code !== 200) {
            throw new Error("Prelogin token failed");
        }
        return response.data.login_session_token;
    };


    const formik = useFormik<FormValues>({
        initialValues: {
            username: '',
            password: '',
            captchaInput: '',
        },
        validationSchema: Yup.object().shape({
            username: Yup.string()
                .required('Username is required'),

            password: Yup.string()
                .required('Password is required'),
        }),
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: async (values: FormValues, { setFieldError }: any) => {
            setLoading(true);

            if (!values.captchaInput.trim()) {
                setFieldError("captchaInput", "Captcha is required");
                setLoading(false);
                return;
            }

            try {
                const loginSessionToken = await getPreloginToken();
                const md5Hashed = CryptoJS.MD5(values.password).toString();
                const sha256HashedPassword = CryptoJS.SHA256(md5Hashed).toString();
                const millisecond = new Date().getTime();
                const preAuthLast16 = loginSessionToken.slice(-16);
                const combinedValue = preAuthLast16 + sha256HashedPassword + millisecond;
                const response = await publicApi.post(
                    `${baseUrl}api/login`,
                    {
                        userName: values.username,
                        password: combinedValue,
                        captcha_id: captchaId,
                        captcha_text: values.captchaInput,
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                             "Pre-Auth-token": loginSessionToken,
                        },
                        withCredentials: true,
                    }
                );

                const msgCode = response?.data?.msg_code;

                if (msgCode === 200) {
                    const portalType = "S";

                    const tokenObj = {
                        access_token: response.data.access_token,
                        token_type: response.data.token_type,
                    };

                    localStorage.setItem("portal", portalType);

                    const {
                        access_token,
                        token_type,
                        ...loginDetailsWithoutToken
                    } = response.data;

                    localStorage.setItem(
                        "loginDetails",
                        JSON.stringify(loginDetailsWithoutToken)
                    );

                    dispatch(
                        login({
                            portal: portalType,
                            meetingDetailsNirnany: null,
                            token: tokenObj,
                        })
                    );

                    window.scrollTo(0, 0);
                    navigate("/afterLogin");
                    return;
                }
                toast.error(errorMessages[msgCode] || "Login failed");
                resetCaptcha();
            } catch (error) {
                toast.error("Login failed. Please try again.");
                resetCaptcha();
            } finally {
                setLoading(false);
            }
        }
    } as any);


    return (
        <Wrapper>
            <div className="p-3">
                <div className="login-form">
                    <div className="login-form-heading">
                        <div className="login-text"> <FaLock className="lock-icon" />
                            <h3>Log in to SabhaSaar</h3></div>
                        <p className='mb-0 text-center'>(Login with e-GramSwaraj ADM Credentials) </p>
                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="form p-3">
                            <div className="form-group">
                                <label htmlFor='username' className=" form-label my-2">Username</label>
                                <div className="input-group">
                                    <span className="input-group-text"><FaUser /></span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="username"
                                        name="username"
                                        value={formik.values.username}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Please enter your username"
                                        autoComplete="off"
                                        readOnly
                                        onFocus={(e) => e.target.removeAttribute('readonly')}
                                        maxLength={100}
                                    />
                                </div>
                                <span className="error-msg">
                                    {formik.touched.username && formik.errors.username && formik.errors.username}
                                </span>
                            </div>

                            <div className="form-group">
                                <label htmlFor='password' className=" form-label my-2">Password</label>
                                <div className="input-group">
                                    <span className="input-group-text"><FaLock /></span>
                                    <input
                                        ref={passwordInputRef}
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="Enter your password"
                                        name="password"
                                        id='password'
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        autoComplete='on'
                                        maxLength={50}
                                    />
                                    <span
                                        className="input-group-text"
                                        style={{ cursor: 'pointer' }}
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </div>
                                <span className="error-msg">
                                    {formik.touched.password && formik.errors.password && formik.errors.password}
                                </span>
                            </div>

                            <div className="form-group">
                                <label className=" form-label my-2">Captcha</label>
                                <Captcha
                                    ref={captchaRef}
                                    onChange={(id) => {
                                        setCaptchaId(id);
                                        formik.setFieldValue("captchaInput", "");
                                    }}
                                />
                                <input
                                    type="text"
                                    className="form-control mt-2"
                                    placeholder="Enter captcha shown above"
                                    name="captchaInput"
                                    value={formik.values.captchaInput}
                                    onChange={formik.handleChange}
                                    maxLength={6}
                                />
                            </div>
                            <span className="error-msg">
                                {formik.touched.captchaInput && formik.errors.captchaInput && formik.errors.captchaInput}
                            </span>

                            <button type="submit" className="cus-login-btn mt-3" disabled={loading}>
                                <FaSignOutAlt /> {loading ? 'Wait ...' : 'Login'}
                            </button>
                            <button className="cus-reset-btn mt-2" onClick={() => formik.resetForm()}>
                                <FaUndo /> Reset
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Wrapper>
    );
};

export default LoginForm;
